import {
  CreateDirectTaskBody,
  DirectTask,
  DirectTaskState,
  TrueworkJsStrategy,
  UpdateTaxInfoRequest,
} from '@frame/lib/api/tasks/types'
import { AttachFilesBody, CompleteDirectTaskBody, UpdateDirectTaskBody } from '@frame/lib/api/tasks/types'
import { Api } from '@frame/lib/api/types'
import * as errors from '@frame/lib/errors'
import { getErrorWithFallback } from '@frame/lib/errors'
import { useStore } from '@frame/lib/stores/useStore'

import { ClientSessionState } from './api/client-session/types'
import { TrueworkJsFeedbackReason } from './api/common/types'
import { PartnerEventPayload } from './api/events/types'
import { events } from './events'
import { useFeatureFlagsStore as flagsStore } from './stores/useFeatureFlagStore'

export type Actions = ReturnType<typeof create>

export function create({ api }: { api: Api }) {
  return {
    async getSession() {
      const { setState, getState } = useStore
      const { data, error } = await api.clientSession.getClientSession()

      if (!data) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to load data for getClientSession')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to start verification',
          setError: getState().setError,
        })
        return
      }

      const existingConfig = getState().session?.configuration

      setState({
        session: {
          ...data,
          configuration: existingConfig || data?.configuration,
        },
      })

      return data
    },
    async configureSession(publishableKey: string): Promise<void> {
      const { setState } = useStore
      const { data, response, error } = await api.clientSession.configureSession(publishableKey)

      if (response?.status === 401) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Invalid or expired session token')),
          code: errors.codes.Unauthorized,
          extra: errors.parseExtra(error),
          setError: (e) => setState({ session: data, error: e }),
        })
        return
      }

      if (!data) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to load data for configureSession')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to start verification',
          setError: (e) => setState({ session: data, error: e }),
        })

        return
      }

      setState({ session: data })
    },
    async gatherSessionConsent() {
      const { setState, getState } = useStore
      const existingConfig = getState().session?.configuration

      const sessionData = await this.getSession()

      if (sessionData && sessionData.state !== ClientSessionState.CONSENT) {
        setState({
          session: {
            ...sessionData,
            configuration: existingConfig || sessionData.configuration,
          },
        })
        return
      }

      const { data, error } = await api.clientSession.gatherSessionConsent()

      if (!data) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to load data for gatherSessionConsent')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to save consent',
          setError: getState().setError,
        })
        return
      }

      setState({
        session: {
          ...data,
          configuration: existingConfig || data.configuration,
        },
      })
    },
    async manuallyTriggerIES() {
      const { setState, getState } = useStore
      const { data, error } = await api.clientSession.manuallyTriggerIES()
      if (!data) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to trigger IES')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to start verification',
          setError: getState().setError,
        })
        return
      }

      setState({ session: data })
    },
    async completeDirectTask({
      directTaskId,
      completeDirectTaskBody,
      multiEmployer = false,
    }: {
      directTaskId?: string
      completeDirectTaskBody: CompleteDirectTaskBody
      multiEmployer?: boolean
    }) {
      const { getState } = useStore

      try {
        if (!directTaskId) throw new Error('Missing directTaskId')

        const { data, error } = await api.tasks.completeDirectTask(directTaskId, completeDirectTaskBody)

        if (!data) {
          errors.create({
            error: getErrorWithFallback(error, new Error('Failed to load data for completeDirectTask')),
            code: errors.codes.Network,
            extra: errors.parseExtra(error),
            displayReason: 'Failed to save task details',
            setError: getState().setError,
          })

          return null
        }

        if (!multiEmployer) {
          events.emit('complete')
        }

        await this.getSession()

        return data
      } catch (error) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to complete direct task')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          setError: getState().setError,
        })
      }
    },
    async updateDirectTask(directTaskId: string, updateDirectTaskBody: UpdateDirectTaskBody) {
      const { data, error, response } = await api.tasks.updateDirectTask(directTaskId, updateDirectTaskBody)
      const { getState } = useStore

      if (error || !data || !response.ok) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to load data for updateDirectTask')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to save task details',
          setError: getState().setError,
        })
        return
      }

      await this.getSession()

      return data
    },
    async prepareTask(directTaskId: string) {
      const session = await this.getSession()
      const { setState, getState } = useStore
      const tasks = session?.tasks || []
      const task = tasks.find((e) => e.id === directTaskId)

      if (task && task?.strategy_info !== null && task.state === DirectTaskState.READY) {
        return task
      }

      const { data, error } = await api.tasks.verifyTask(directTaskId)

      if (!data) {
        errors.create({
          error: new Error('Failed to load data from verifyTask'),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to verify task',
          setError: getState().setError,
        })

        return
      }

      if (session) {
        setState({
          session: {
            ...session,
            tasks: session.tasks.map((e) => (e.id === directTaskId ? data : e)),
          },
        })
      }

      return data
    },
    async skipDirectTask(directTaskId: string) {
      const { setState, getState } = useStore

      const { data, error } = await api.tasks.skipDirectTask(directTaskId)

      if (!data) {
        errors.create({
          error: new Error('Failed to skip direct task'),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          setError: getState().setError,
        })

        return
      }

      const session = await this.getSession()

      if (session) {
        setState({ session })
      }

      return session?.tasks.find((e) => e.id === directTaskId)
    },
    async unSkipDirectTask(directTaskId: string) {
      const { setState, getState } = useStore

      const { data, error } = await api.tasks.unSkipDirectTask(directTaskId)

      if (!data || data?.state !== DirectTaskState.READY) {
        errors.create({
          error: new Error('Failed to continue verification for direct task'),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          setError: getState().setError,
        })

        return
      }

      const session = await this.getSession()

      if (session) {
        setState({ session })
      }

      return session?.tasks.find((e) => e.id === directTaskId)
    },
    async createDirectTask(createDirectTaskBody: CreateDirectTaskBody) {
      const { getState, setState } = useStore

      const { data, error } = await api.tasks.createDirectTask(createDirectTaskBody)

      const session = await this.getSession()

      if (!data) {
        errors.create({
          error: new Error('Failed to load data for createDirectTask'),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to save task details',
          setError: getState().setError,
        })
        return
      }

      if (session) {
        setState({
          session: {
            ...session,
            tasks: session.tasks.map((e) => (e.id === data.id ? data : e)),
          },
        })
      }
      await this.getSession()

      return data
    },
    async attachFilesToDirectTask(directTaskId: string, attachFilesBody: AttachFilesBody) {
      const { setState, getState } = useStore

      const { data, error } = await api.tasks.attachFiles(directTaskId, attachFilesBody)

      const session = await this.getSession()

      if (!data) {
        errors.create({
          error: new Error('Failed to load data from attachFilesToDirectTask'),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: 'Failed to attach files to task details',
          setError: getState().setError,
        })
        return
      }

      if (session) {
        setState({
          session: {
            ...session,
            tasks: session.tasks.map((e) => (e.id === directTaskId ? data : e)),
          },
        })
      }
    },
    async addStrategyToDirectTask({
      directTaskId,
      strategy,
    }: {
      directTaskId?: string
      strategy: TrueworkJsStrategy
    }): Promise<DirectTask | undefined> {
      const { getState } = useStore

      const error = {
        message: 'Failed when attempting to add strategy to task',
        displayReason: 'Failed to start verification',
      }

      if (!directTaskId) {
        errors.create({
          error: new Error(error.message),
          code: errors.codes.Network,
          displayReason: error.displayReason,
          setError: getState().setError,
        })
        return
      }
      const updatedTask = await this.updateDirectTask(directTaskId, {
        strategy,
      })

      if (!updatedTask) {
        errors.create({
          error: new Error(error.message),
          code: errors.codes.Network,
          displayReason: error.displayReason,
          setError: getState().setError,
        })
        return
      }

      const preparedTask = await this.prepareTask(updatedTask.id)

      if (!preparedTask) {
        errors.create({
          error: new Error(error.message),
          code: errors.codes.Network,
          displayReason: error.displayReason,
          setError: getState().setError,
        })
        return
      }

      return preparedTask
    },
    async monitorPartnerEvent(payload: PartnerEventPayload) {
      return api.events.sendPartnerEvent(payload)
    },
    async completeSession() {
      const { setState, getState } = useStore
      const { data, response } = await api.clientSession.completeSession()

      if (!data || response.status >= 400) {
        errors.create({
          error: new Error(`Failed to complete session. Got HTTP status code of ${status} from backend.`),
          code: errors.codes.Network,
          displayReason: 'Failed to complete verification',
          setError: getState().setError,
        })
        return
      }

      setState({ session: data })
    },
    async hydrateFeatureFlags() {
      const { data } = await api.common.getFeatureFlags()

      if (data) {
        flagsStore.getState().setFlags(data.experiment_results ?? {})
      }
    },
    async addFeedback({
      reason,
      instanceId,
      otherReason,
    }: {
      reason: TrueworkJsFeedbackReason
      instanceId: string
      otherReason?: string
    }) {
      const res = await api.common.addFeedback({
        feedback: reason,
        instance_id: instanceId,
        other_feedback: otherReason,
      })
      await this.getSession()

      return res
    },
    async persistTaxInfo(directTaskId: string, request: UpdateTaxInfoRequest) {
      const { error, response } = await api.tasks.persistTaxInfo(directTaskId, request)
      const { getState } = useStore

      if (error || !response.ok) {
        errors.create({
          error: getErrorWithFallback(error, new Error('Failed to load data for persistTaxInfo')),
          code: errors.codes.Network,
          extra: errors.parseExtra(error),
          displayReason: error?.error?.message || 'Failed to save tax details',
          setError: getState().setError,
        })
      }
    },
  }
}
